import { calculateAll } from './calculator';
import type { CalculatorInputs, PriceLevel } from './types';

/**
 * The 5 standard offer price levels relative to asking price.
 */
export const PRICE_LEVEL_DELTAS = [
  { label: 'Strong Offer', pctDelta: 0.05 },
  { label: 'Above Asking', pctDelta: 0.02 },
  { label: 'At Asking', pctDelta: 0 },
  { label: 'Below Asking', pctDelta: -0.03 },
  { label: 'Low Offer', pctDelta: -0.05 },
] as const;

/**
 * Compute the 5 price levels for a given asking price and calculator inputs.
 * Returns an array of PriceLevel objects with monthly payment and cash to close
 * pre-computed for each level.
 */
export function computePriceLevels(
  askingPrice: number,
  baseInputs: CalculatorInputs
): PriceLevel[] {
  return PRICE_LEVEL_DELTAS.map(({ label, pctDelta }) => {
    const offerPrice = Math.round(askingPrice * (1 + pctDelta));

    // Create modified inputs at this price point
    const inputs: CalculatorInputs = {
      ...baseInputs,
      homePrice: offerPrice,
      downPaymentAmt: Math.round(offerPrice * (baseInputs.downPaymentPct / 100)),
    };

    const results = calculateAll(inputs);

    return {
      label,
      pctDelta,
      offerPrice,
      monthlyPayment: results.totalMonthly,
      cashToClose: results.cashToClose.cashToClose,
    };
  });
}

/**
 * Determine affordability indicator for a cash-to-close value
 * against the buyer's available cash on hand.
 */
export function getCashIndicator(
  cashToClose: number,
  cashOnHand: number
): 'green' | 'yellow' | 'red' {
  if (cashOnHand <= 0) return 'green'; // No data, don't show warning
  if (cashToClose <= cashOnHand * 0.9) return 'green';
  if (cashToClose <= cashOnHand) return 'yellow';
  return 'red';
}

/**
 * Get a DTI-based color indicator for monthly payment affordability.
 */
export function getPaymentIndicator(
  monthlyPayment: number,
  grossMonthlyIncome: number
): 'green' | 'yellow' | 'red' {
  if (grossMonthlyIncome <= 0) return 'green';
  const frontDti = (monthlyPayment / grossMonthlyIncome) * 100;
  if (frontDti <= 28) return 'green';
  if (frontDti <= 36) return 'yellow';
  return 'red';
}

// ── Affordability threshold computation ──────────────────────────────

type IndicatorColor = 'green' | 'yellow' | 'red';

interface MetricThresholds {
  colorAtMin: IndicatorColor;
  colorAtMax: IndicatorColor;
  /** pctDelta where green→yellow transition occurs (null if it doesn't happen in range) */
  greenYellowDelta: number | null;
  /** pctDelta where yellow→red transition occurs (null if it doesn't happen in range) */
  yellowRedDelta: number | null;
}

export interface AffordabilityThresholds {
  payment: MetricThresholds;
  cash: MetricThresholds;
  atAsking: {
    monthlyPayment: number;
    cashToClose: number;
  };
}

/** Compute monthly payment and cash-to-close at a given percentage delta from asking. */
export function computeAtDelta(
  askingPrice: number,
  baseInputs: CalculatorInputs,
  pctDelta: number
): { monthlyPayment: number; cashToClose: number } {
  const offerPrice = Math.round(askingPrice * (1 + pctDelta));
  const inputs: CalculatorInputs = {
    ...baseInputs,
    homePrice: offerPrice,
    downPaymentAmt: Math.round(offerPrice * (baseInputs.downPaymentPct / 100)),
  };
  const results = calculateAll(inputs);
  return {
    monthlyPayment: results.totalMonthly,
    cashToClose: results.cashToClose.cashToClose,
  };
}

/**
 * Binary-search for the pctDelta where a predicate flips from true to false.
 * Assumes monotonicity: predicate is true at `lo` and false at `hi`.
 */
function bisect(
  lo: number,
  hi: number,
  predicate: (delta: number) => boolean,
  precision = 0.001
): number {
  for (let i = 0; i < 20 && hi - lo > precision; i++) {
    const mid = (lo + hi) / 2;
    if (predicate(mid)) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

/**
 * Find green→yellow and yellow→red thresholds for a metric across the delta range.
 * Both payment and cash are monotonically increasing with price, so colors only
 * progress green → yellow → red as delta increases.
 */
function findMetricThresholds(
  minDelta: number,
  maxDelta: number,
  colorAt: (delta: number) => IndicatorColor
): { colorAtMin: IndicatorColor; colorAtMax: IndicatorColor; greenYellowDelta: number | null; yellowRedDelta: number | null } {
  const colorAtMin = colorAt(minDelta);
  const colorAtMax = colorAt(maxDelta);

  let greenYellowDelta: number | null = null;
  let yellowRedDelta: number | null = null;

  // Green→yellow: exists if min is green and max is yellow or red
  if (colorAtMin === 'green' && colorAtMax !== 'green') {
    greenYellowDelta = bisect(minDelta, maxDelta, d => colorAt(d) === 'green');
  }

  // Yellow→red: exists if max is red and min is green or yellow
  if (colorAtMax === 'red' && colorAtMin !== 'red') {
    const searchFrom = greenYellowDelta ?? minDelta;
    yellowRedDelta = bisect(searchFrom, maxDelta, d => colorAt(d) !== 'red');
  }

  return { colorAtMin, colorAtMax, greenYellowDelta, yellowRedDelta };
}

/**
 * Compute where the green/yellow/red affordability thresholds fall across the
 * -5% to +5% offer range. Uses binary search — monotonic since payment and cash
 * both increase with price.
 */
export function computeAffordabilityThresholds(
  askingPrice: number,
  baseInputs: CalculatorInputs,
  cashOnHand: number,
  grossMonthlyIncome: number
): AffordabilityThresholds {
  const MIN_DELTA = -0.05;
  const MAX_DELTA = 0.05;

  const atAsk = computeAtDelta(askingPrice, baseInputs, 0);

  const payColorAt = (d: number): IndicatorColor => {
    const v = computeAtDelta(askingPrice, baseInputs, d);
    return getPaymentIndicator(v.monthlyPayment, grossMonthlyIncome);
  };
  const cashColorAt = (d: number): IndicatorColor => {
    const v = computeAtDelta(askingPrice, baseInputs, d);
    return getCashIndicator(v.cashToClose, cashOnHand);
  };

  return {
    payment: findMetricThresholds(MIN_DELTA, MAX_DELTA, payColorAt),
    cash: findMetricThresholds(MIN_DELTA, MAX_DELTA, cashColorAt),
    atAsking: {
      monthlyPayment: atAsk.monthlyPayment,
      cashToClose: atAsk.cashToClose,
    },
  };
}
